import { catalogIndex } from "./catalog-index"
import { planFor } from "./compose"
import { bausteinCostUsd } from "./economics"
import { evidenceIndex, meetsReactionTarget, provenInstrument } from "./evidence"
import { byId, byText } from "./order"
import { resolvePolicy } from "./policy"
import type {
    Catalog,
    Constraints,
    EmployeeProfile,
    EvidenceInstrument,
    Modality,
} from "./types"

/**
 * Population-level aggregation for the L&D cockpit.
 *
 * Pure, like the rest of the engine, and fast enough to run live: the full
 * 1,960-person demo population composes in ~80 ms, so the cockpit recomputes on
 * every lever change rather than serving a precomputed snapshot.
 */

export interface CoverageRow {
    obligationId: string
    label: string
    basis: string
    /** People the obligation binds. */
    bound: number
    /** Of those, how many are currently compliant. */
    compliant: number
    /** Of those, how many are already past the deadline. */
    overdue: number
    coverage: number
}

export interface SkillGapRow {
    skillId: string
    label: string
    category: string
    /** People with an open gap in this skill. */
    affected: number
    /** Total level-steps missing across the population. */
    severity: number
    /** True when NO Baustein in the catalog teaches this skill. */
    uncovered: boolean
}

export interface PopulationSummary {
    headcount: number
    /** People with at least one open mandatory item. */
    withOpenObligation: number
    /** People with at least one OVERDUE mandatory item. */
    withOverdue: number
    mandatoryCoverage: number
    coverage: CoverageRow[]
    skillGaps: SkillGapRow[]
    /** Total planned hours across the population, and the FTE-equivalent. */
    totalPlannedHours: number
    /** Planned hours by org unit, largest first. */
    byOrgUnit: Array<{ orgUnitLabel: string; headcount: number; hours: number; overdue: number }>
    /** Bausteine most frequently planned, largest first. */
    demand: Array<{ bausteinId: string; title: string; count: number; hours: number }>

    /**
     * Francs of planned training, priced by the policy rate card.
     *
     * Without this, selecting Phillips ROI changes the ranking but moves no
     * visible number — a framework whose entire subject is money, reported only
     * in hours.
     */
    totalCostUsd: number

    /**
     * What the transcript actually proves, counted over every completion.
     *
     * This is the LTEM question made into a number: how much of "completed" is
     * merely attendance? It does not depend on the evidence GATES being on —
     * you can read the honest answer before deciding to act on it.
     */
    evidenceMix: Record<EvidenceInstrument, number>

    /**
     * Distribution of planned hours across the population, in fixed bins.
     *
     * An average hides the shape, and the shape is where policy changes show up:
     * a stricter evidence floor barely moves the median while doubling the tail.
     * Bins are FIXED rather than derived from the data so two summaries can be
     * overlaid — a histogram whose bins move with its own maximum cannot be
     * compared to another one.
     */
    hoursHistogram: number[]

    /**
     * Where the planned hours flow: requirement origin → modality.
     *
     * Enough to drive a Sankey without shipping per-person data. Kept as a flat
     * list rather than a nested object so it stays stable under JSON and needs
     * no key ordering guarantees.
     */
    flow: Array<{ origin: string; modality: Modality; hours: number; count: number }>

    /** How far up the Kirkpatrick chain the evidence reaches, over all completions. */
    kirkpatrick: {
        completions: number
        /** L1 reaction recorded. */
        reaction: number
        /** L1 recorded AND at or above the "likelihood to apply" target. */
        reactionOnTarget: number
        /** L2 assessed and passed at the policy threshold. */
        learning: number
        /** L2→3 bridge: a deliverable that met its criteria. */
        bridge: number
        /** L3 confirmed as applied or partly applied. */
        behaviour: number
        /** Of those, the ones a manager verified rather than self-report alone. */
        behaviourVerified: number
    }
}

/**
 * Upper bound of each histogram bin, in hours. Open-ended at the top.
 *
 * Chosen against the observed range (most plans land under 40 h, the tail runs
 * past 100) and then FROZEN: comparability between two summaries is the whole
 * point, so these must not adapt to the data.
 */
const HOURS_BINS = [5, 10, 15, 20, 25, 30, 40, 50, 65, 80, 100, Number.POSITIVE_INFINITY]

function binFor(hours: number): number {
    const index = HOURS_BINS.findIndex((upper) => hours < upper)
    return index === -1 ? HOURS_BINS.length - 1 : index
}

export const HOURS_BIN_EDGES = HOURS_BINS

export function summarisePopulation(
    employees: EmployeeProfile[],
    catalog: Catalog,
    constraints: Constraints,
): PopulationSummary {
    const { bausteinById, skillById, teachableSkills } = catalogIndex(catalog)

    const coverage = new Map<string, CoverageRow>()
    for (const obligation of catalog.obligations) {
        coverage.set(obligation.id, {
            obligationId: obligation.id,
            label: obligation.label,
            basis: obligation.basis,
            bound: 0,
            compliant: 0,
            overdue: 0,
            coverage: 0,
        })
    }

    const gapTally = new Map<string, { affected: number; severity: number }>()
    const unitTally = new Map<string, { headcount: number; hours: number; overdue: number }>()
    const demandTally = new Map<string, { title: string; count: number; hours: number }>()

    let withOpenObligation = 0
    let withOverdue = 0
    let totalPlannedHours = 0
    let totalCostUsd = 0

    const policy = resolvePolicy(constraints.policy)
    const histogram = new Array<number>(HOURS_BINS.length).fill(0)
    const flowTally = new Map<string, { origin: string; modality: Modality; hours: number; count: number }>()
    const evidenceMix: Record<EvidenceInstrument, number> = {
        abschluss: 0,
        wissenstest: 0,
        entscheidungstest: 0,
        transfernachweis: 0,
    }
    const chain = {
        completions: 0,
        reaction: 0,
        reactionOnTarget: 0,
        learning: 0,
        bridge: 0,
        behaviour: 0,
        behaviourVerified: 0,
    }

    for (const employee of employees) {
        // One pass. `composeCurriculum` derives the gaps and the due obligations
        // itself, so calling all three separately made the engine compute them
        // twice and the current levels three times, per employee, per request.
        const { curriculum, gaps, dueObligations: due } = planFor(employee, catalog, constraints)

        const dueIds = new Set(due.map((d) => d.obligation.id))
        const overdueIds = new Set(due.filter((d) => d.overdue).map((d) => d.obligation.id))

        for (const obligation of catalog.obligations) {
            const binds =
                obligation.appliesToRoles.includes(employee.roleId) ||
                (obligation.appliesToSigningAuthority &&
                    employee.signingAuthority &&
                    obligation.appliesToSigningAuthority.includes(employee.signingAuthority))
            if (!binds) continue

            const row = coverage.get(obligation.id)!
            row.bound += 1
            if (!dueIds.has(obligation.id)) row.compliant += 1
            if (overdueIds.has(obligation.id)) row.overdue += 1
        }

        if (due.length > 0) withOpenObligation += 1
        if (due.some((d) => d.overdue)) withOverdue += 1

        for (const gap of gaps) {
            const tally = gapTally.get(gap.skillId) ?? { affected: 0, severity: 0 }
            tally.affected += 1
            tally.severity += gap.size
            gapTally.set(gap.skillId, tally)
        }

        totalPlannedHours += curriculum.totalHours

        const unit = unitTally.get(employee.orgUnitLabel) ?? { headcount: 0, hours: 0, overdue: 0 }
        unit.headcount += 1
        unit.hours += curriculum.totalHours
        if (due.some((d) => d.overdue)) unit.overdue += 1
        unitTally.set(employee.orgUnitLabel, unit)

        histogram[binFor(curriculum.totalHours)] += 1

        for (const item of curriculum.items) {
            totalCostUsd += bausteinCostUsd(item.baustein, policy)

            // Attribute each item's hours to the origin that put it in the plan.
            // `pflicht` and `voraussetzung` are not gap origins, so they are
            // reported under their reason — otherwise a mandatory chain would
            // vanish from the flow entirely.
            const origin =
                item.reason === "pflicht" || item.reason === "voraussetzung"
                    ? item.reason
                    : (item.closesGaps[0]?.origin ?? "rolle")
            const modality = policy.mix.modalityOf[item.baustein.format]
            const key = `${origin}|${modality}`
            const flowEntry = flowTally.get(key) ?? { origin, modality, hours: 0, count: 0 }
            flowEntry.hours += item.baustein.durationHours
            flowEntry.count += 1
            flowTally.set(key, flowEntry)

            const entry = demandTally.get(item.baustein.id) ?? {
                title: item.baustein.title,
                count: 0,
                hours: 0,
            }
            entry.count += 1
            entry.hours += item.baustein.durationHours
            demandTally.set(item.baustein.id, entry)
        }

        // The Kirkpatrick chain, read off what is already known rather than off
        // what the gates are currently set to. HR has to be able to see the
        // evidence picture BEFORE deciding to enforce a standard against it.
        const evidence = evidenceIndex(employee.evidence)
        for (const completion of employee.completed) {
            const baustein = bausteinById.get(completion.bausteinId)
            if (!baustein) continue
            const record = evidence.get(completion.bausteinId)
            chain.completions += 1
            evidenceMix[provenInstrument(baustein, record, policy)] += 1
            if (record?.reaction) chain.reaction += 1
            if (meetsReactionTarget(record, policy)) chain.reactionOnTarget += 1
            if (
                record?.learning?.passed &&
                record.learning.scaled >= policy.evidence.learningPassScaled
            ) {
                chain.learning += 1
            }
            if (record?.bridge?.meetsCriteria) chain.bridge += 1
            if (record?.behaviour && record.behaviour.applied !== "no") {
                chain.behaviour += 1
                if (record.behaviour.verifiedBy) chain.behaviourVerified += 1
            }
        }
    }

    for (const row of coverage.values()) {
        row.coverage = row.bound === 0 ? 1 : row.compliant / row.bound
    }

    const skillGaps: SkillGapRow[] = [...gapTally.entries()]
        .map(([skillId, tally]) => ({
            skillId,
            label: skillById.get(skillId)?.label ?? skillId,
            category: skillById.get(skillId)?.category ?? "",
            affected: tally.affected,
            severity: tally.severity,
            uncovered: !teachableSkills.has(skillId),
        }))
        .sort(
            (a, b) =>
                b.affected - a.affected || byText(a.label, b.label) || byId(a.skillId, b.skillId),
        )

    return {
        headcount: employees.length,
        withOpenObligation,
        withOverdue,
        mandatoryCoverage:
            employees.length === 0 ? 1 : (employees.length - withOpenObligation) / employees.length,
        // Every one of these three sorts feeds a list that is truncated or read
        // top-down, and every one of them tallies in EMPLOYEE-ITERATION order —
        // so without a tiebreak, two units with equal hours land in whichever
        // order the population happened to produce, and the top-12 cut can flip.
        // Harmless while the roster is frozen; not harmless the moment a policy
        // knob reshuffles the counts.
        coverage: [...coverage.values()]
            .filter((row) => row.bound > 0)
            .sort((a, b) => a.coverage - b.coverage || byId(a.obligationId, b.obligationId)),
        skillGaps,
        totalPlannedHours: Math.round(totalPlannedHours),
        byOrgUnit: [...unitTally.entries()]
            .map(([orgUnitLabel, tally]) => ({ orgUnitLabel, ...tally, hours: Math.round(tally.hours) }))
            .sort((a, b) => b.hours - a.hours || byText(a.orgUnitLabel, b.orgUnitLabel))
            .slice(0, 12),
        demand: [...demandTally.entries()]
            .map(([bausteinId, entry]) => ({ bausteinId, ...entry, hours: Math.round(entry.hours) }))
            .sort(
                (a, b) =>
                    b.count - a.count || byText(a.title, b.title) || byId(a.bausteinId, b.bausteinId),
            )
            .slice(0, 12),
        totalCostUsd: Math.round(totalCostUsd),
        hoursHistogram: histogram,
        flow: [...flowTally.values()]
            .map((entry) => ({ ...entry, hours: Math.round(entry.hours) }))
            // Total order, like every other list here: the flow feeds a layout
            // whose node order would otherwise depend on employee iteration.
            .sort((a, b) => byId(a.origin, b.origin) || byId(a.modality, b.modality)),
        evidenceMix,
        kirkpatrick: chain,
    }
}
